import { useCallback, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useNavigate } from 'react-router-dom'
import { useCurrentUser, usePermissions } from '@/features/auth/state/auth'
import NoData from '@/assets/no-data.svg?react'
import useClients from '@/services/useClients'
import { formatCNPJ, formatNome } from '@/utils/format'
import { formatCPF } from '@/components/form/input/cpf-mask-input'

import { PageLayout } from '@/layouts'
import { ROUTE_BUILDERS } from '@/config/routes'
import { useTenantContext } from '@/context/tenant'
import type { ClientItem } from '@/types/client'
import { useToast } from '@/lib/hooks/use-toast'
import { useFetchClientsByProduct } from '@/features/products/services/useFetchClientsByProduct'
import ClientStatusTag from './client-status-tag'
import { Button } from '@/components/ui/button'
import { FileSpreadsheet, Loader2 } from 'lucide-react'
import { useExportClientsTenantReport } from '../services/useExportClientsTenantReport'

type StatusFilter =
  | 'TODOS'
  | 'ATIVO'
  | 'PENDENTE'
  | 'INATIVO'
  | 'INADIMPLENTE'
  | 'NOVO'
  | 'JURIDICO'
  | 'SEM_OPORTUNIDADE'
  | 'PROCURACAO_CANCELADA'
  | 'PROCURACAO_EXPIRADA'

export default function ListarCliente({ id_tenant }: { id_tenant?: string | null }) {
  const user = useCurrentUser()
  const permissions = usePermissions()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { selectedTenant } = useTenantContext()
  const { mutate: exportTenantReport, isPending: isExportingReport } = useExportClientsTenantReport()
  const canExportTenantReport =
    !!permissions?.export_planilha_clients &&
    user?.typeCargo === 'CEO' &&
    (user?.typeUser === 'ADMIN' || user?.id_tenant === id_tenant)

  const { data: clientsByUser = [] } = useFetchClientsByProduct(user?.id, id_tenant || '', { enabled: !!id_tenant })
  const { clients: allClients, setSearchClient: setSearchTerm } = useClients({ user, id_tenant })

  const [currentPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('TODOS')
  const [searchTerm, setSearchTermState] = useState('')

  const canNavigateToClient = useCallback(
    (client: ClientItem) => {
      if (user?.typeUser === 'ADMIN') {
        if (permissions?.permission_product || permissions?.permission_geral) return true
        else if (user?.typeCargo === 'CEO') return true
        else if (user?.typeCargo === 'ANALISTA') {
          return clientsByUser.some((p: any) => String(client.id_client) === String(p.id_client))
        }
      } else if (user?.typeUser === 'USUARIO') {
        if (user?.typeCargo === 'CEO' && user?.id_tenant === client.id_tenant) return true
        else if (
          (user?.id === client.id_responsavel || user?.id === client.id_gerente) &&
          user?.id_tenant === client.id_tenant
        ) {
          return true
        }
      }
      return false
    },
    [user, permissions, clientsByUser]
  )

  const filteredClients = allClients.filter(c => (statusFilter === 'TODOS' ? true : c.status === statusFilter))

  const itemsPerPage = 50
  const startIndex = (currentPage - 1) * itemsPerPage
  const currentClients = filteredClients.slice(startIndex, startIndex + itemsPerPage)

  const handleNavigateCliente = (client: ClientItem) => {
    if (!canNavigateToClient(client)) {
      toast({ variant: 'destructive', description: 'Você não tem permissão para acessar este cliente.' })
      return
    }
    if (selectedTenant && user?.typeUser === 'ADMIN') {
      navigate(ROUTE_BUILDERS.clientDashboardAdminInfo(selectedTenant.id, client.id_client || client.cnpjcpf), {
        state: { client }
      })
    } else if (user?.typeUser === 'USUARIO') {
      navigate(ROUTE_BUILDERS.clientDashboardInfo(client.id_client || client.cnpjcpf), { state: { client } })
    }
  }
  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value)
  }

  const handleExportReport = () => {
    if (!id_tenant) {
      toast({ variant: 'destructive', description: 'Selecione um tenant para exportar o relatório.' })
      return
    }

    exportTenantReport(
      {
        id_tenant,
        tenantDisplayName: selectedTenant?.title || id_tenant,
        status: statusFilter,
        search: searchTerm
      },
      {
        onError: (error: Error) => {
          toast({
            variant: 'destructive',
            description: error.message || 'Não foi possível exportar o relatório.'
          })
        }
      }
    )
  }

  return (
    <PageLayout
      title="Lista de Clientes"
      description={
        <div className="flex flex-col md:flex-row justify-between mb-4 gap-4">
          <div className="flex flex-1 flex-col gap-4 md:flex-row">
            <Input
              placeholder="Buscar por razão social, CNPJ/CPF, proprietário ou CPF do proprietário..."
              onChange={e => {
                const value = e.target.value
                setSearchTermState(value)
                setSearchTerm(value)
              }}
              className="md:w-1/3 bg-input"
            />
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="ATIVO">Ativo</SelectItem>
                <SelectItem value="NOVO">Novo</SelectItem>
                <SelectItem value="INADIMPLENTE">Inadimplente</SelectItem>
                <SelectItem value="INATIVO">Inativo</SelectItem>
                <SelectItem value="JURIDICO">Jurídico</SelectItem>
                <SelectItem value="PENDENTE">Pendente</SelectItem>
                <SelectItem value="PROCURACAO_CANCELADA">Procuração Cancelada</SelectItem>
                <SelectItem value="PROCURACAO_EXPIRADA">Procuração Expirada</SelectItem>
                <SelectItem value="SEM_OPORTUNIDADE">Sem Oportunidade</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {canExportTenantReport && (
            <Button
              type="button"
              variant="outline"
              onClick={handleExportReport}
              disabled={isExportingReport || !id_tenant}
            >
              {isExportingReport ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />}
              Exportar XLSX
            </Button>
          )}
        </div>
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px] md:w-[300px]">Razão Social</TableHead>
            <TableHead className="hidden md:block ">CNPJ</TableHead>
            <TableHead className="hidden lg:table-cell">Proprietário</TableHead>
            <TableHead className="hidden lg:table-cell">CPF Proprietário</TableHead>
            <TableHead className="table-cell">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {currentClients.map(client => (
            <TableRow
              key={client.id_client}
              className="cursor-pointer hover:bg-muted transition"
              onClick={() => handleNavigateCliente(client)}
            >
              <TableCell className="font-medium">{client.razao_social || 'N/A'}</TableCell>
              <TableCell className="hidden text-center md:block">
                {formatCNPJ(String(client.cnpjcpf)) || formatCNPJ(String(client.id_client)) || 'N/A'}
              </TableCell>
              <TableCell className="hidden lg:table-cell">{client.nomeProprietario ? formatNome(client.nomeProprietario) : 'N/A'}</TableCell>
              <TableCell className="hidden lg:table-cell">
                {client.cpfProprietario ? formatCPF(String(client.cpfProprietario)) : 'N/A'}
              </TableCell>
              <TableCell className="font-medium">
                <ClientStatusTag status={client.status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {currentClients.length === 0 && (
        <div className="mt-4 flex flex-col items-center">
          <NoData width={180} className="h-auto" />
          <p className="mt-4 text-lg text-foreground">Nenhum cliente encontrado.</p>
        </div>
      )}
    </PageLayout>
  )
}
